import moment from 'moment';
import lodash from 'lodash';
import { DATE_FORMAT_DEFAULT, DATE_FORMAT_DEFAULT_NO_SECONDS, DATE_FORMAT_DEFAULT_NO_TIME, YEAR_FIRST_DATE_FORMAT_DEFAULT, YEAR_FIRST_DATE_FORMAT_DEFAULT_NO_SECONDS, YEAR_FIRST_DATE_FORMAT_DEFAULT_NO_TIME } from '../../appConstants';
import { IOptionType } from '../../@types/model/optionType';

export default class DateHelper {
    /**
     * Converts date to local time and
     * returns a date string formatted to the user preferred format.
     * @param date
     * @param type
     */
    public static formatDateUtcToLocal = (date : string, type : 'date' | 'dateTime' | 'dateTimeNoSeconds', formatSelection ?: string) : string => {
        const dateFormat = formatSelection ? formatSelection : 'DD/MM/YYYY';

        switch (dateFormat) {
            case 'DD/MM/YYYY':
                return this.formatDate(date, type);
            case 'YYYY/MM/DD':
                return this.formatYearFirstDate(date, type);
            default:
                return this.formatDate(date, type);
        }
    };

    public static formatDate = (date : string, type : 'date' | 'dateTime' | 'dateTimeNoSeconds') : string => {
        switch (type) {
            case 'date':
                return this.formatDateUtcNoTime(date);
            case 'dateTime':
                return this.formatDateUtc(date);
            case 'dateTimeNoSeconds':
                return this.formatDateUtcNoSeconds(date);
            default:
                return this.formatDateUtc(date);
        }
    };

    public static formatYearFirstDate = (date : string, type : 'date' | 'dateTime' | 'dateTimeNoSeconds') : string => {
        switch (type) {
            case 'date':
                return this.formatYearFirstDateUtcNoTime(date);
            case 'dateTime':
                return this.formatYearFirstDateUtc(date);
            case 'dateTimeNoSeconds':
                return this.formatYearFirstDateUtcNoSeconds(date);
            default:
                return this.formatYearFirstDateUtc(date);
        }
    };

    /**
     * Converts date to utc unix timestamp with milliseconds
     * @param date
     * @param type
     */
    public static getDate = (date : moment.Moment, type : 'from' | 'to') : number => {
        const selectedData = {...date};
        switch (type) {
            case 'from':
                return moment(selectedData).startOf('day').utc().unix() * 1000;
            case 'to':
                return moment(selectedData).endOf('day').utc().unix() * 1000;
        }
    };

    /**
     * Converts date to local time and returns a formatted date string.
     * return format: 'DD/MM/YYYY HH:mm:ss'
     * @param date
     */
    public static formatDateUtc = (date : string) : string => {
        return moment.utc(date).local().format(DATE_FORMAT_DEFAULT);
    };

    /**
     * Converts date to local time and returns a formatted date string.
     * return format: 'DD/MM/YYYY HH:mm'
     * @param date
     */
    public static formatDateUtcNoSeconds = (date : string) : string => {
        return moment.utc(date).local().format(DATE_FORMAT_DEFAULT_NO_SECONDS);
    };

    /**
     * Converts date to local time and returns a formatted date only string.
     * return format: 'DD/MM/YYYY'
     * @param date
     */
    public static formatDateUtcNoTime = (date : string) : string => {
        return moment.utc(date).local().format(DATE_FORMAT_DEFAULT_NO_TIME);
    };

    /**
     * Converts date to local time and returns a formatted date string. (YEAR FIRST)
     * return format: 'YYYY/MM/DD HH:mm:ss'
     * @param date
     */
    public static formatYearFirstDateUtc = (date : string) : string => {
        return moment.utc(date).local().format(YEAR_FIRST_DATE_FORMAT_DEFAULT);
    };

    /**
     * Converts date to local time and returns a formatted date string. (YEAR FIRST)
     * return format: 'YYYY/MM/DD HH:mm'
     * @param date
     */
    public static formatYearFirstDateUtcNoSeconds = (date : string) : string => {
        return moment.utc(date).local().format(YEAR_FIRST_DATE_FORMAT_DEFAULT_NO_SECONDS);
    };

    /**
     * Converts date to local time and returns a formatted date only string. (YEAR FIRST)\
     * return format: 'YYYY/MM/DD'
     * @param date
     */
    public static formatYearFirstDateUtcNoTime = (date : string) : string => {
        return moment.utc(date).local().format(YEAR_FIRST_DATE_FORMAT_DEFAULT_NO_TIME);
    };

    /**
     * Converts date to local time and returns the day of the date.
     * return format: 'DD'
     * @param date
     */
    public static getDay = (date : string) : string => {
        return moment.utc(date).local().format('DD');
    };

    /**
     * Converts date to local time and returns the month of the date.
     * return format: 'MM'
     * @param date
     */
    public static getMonth = (date : string) : string => {
        return moment.utc(date).local().format('MM');
    };

    /**
     * Converts date to local time and returns the time of the date.
     *  return format: 'hh:mm'
     * @param date
     */
    public static getTime = (date : string) : string => {
        return moment.utc(date).local().format('hh:mm');
    };

    /**
     * Returns the number of weeks for the given year.
    */
    public static numberOfWeeksCurrentYear = () : number => {
        return moment().local().weeksInYear();
    };
    
    /**
     * Returns list of week auto select options
    */
    public static weekOptions = () : Array<IOptionType> => {
        const numberOfWeeksCurrentYear = this.numberOfWeeksCurrentYear();

        const weekOptions : Array<IOptionType> = lodash.range(1, numberOfWeeksCurrentYear).map(x => {
            return { label: lodash.padStart(x.toString(), 2, '0'), value: x };
        });

        return weekOptions;
    };
    
    /**
     * Returns list of year auto select options
    */
    public static yearOptions = () : Array<IOptionType> => {
        const currentYear = moment().year();

        return [
            { label: (currentYear - 1).toString(), value: currentYear - 1 },
            { label: currentYear.toString(), value: currentYear},
            { label: (currentYear + 1).toString(), value: currentYear + 1},
            { label: (currentYear + 2).toString(), value: currentYear + 2},
        ];
    };
}
